import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletWriteCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type { WalletDto } from "@/features/wallet/api/dto";
import type { WalletKycLevel } from "@/features/shared/api/enums";

/**
 * Applique l'approbation KYC à un portefeuille
 * (`POST /Wallets/{id}/kyc-approval?kycLevel=...`) — étape 6 du parcours
 * d'inscription : passage en ACTIVE et application des plafonds du palier.
 * Normalement déclenché par Kafka (`kyc.case.approved`) ; aucun consommateur
 * n'étant câblé, cet appel est fait explicitement par le back-office.
 */
export const useApproveWalletKyc = (walletId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "wallets", "kyc-approval", walletId],
    mutationFn: ({ kycLevel }: { kycLevel: WalletKycLevel }) =>
      mutateData({
        url: `${walletEndpoints.wallets.kycApproval(walletId)}?kycLevel=${kycLevel}`,
        method: "POST",
      }) as Promise<WalletDto>,
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        walletWriteCacheKeys(walletId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useApproveWalletKyc failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
